import { apiMethodContext } from "libs/utils/http/builder/UrlConfig";
import { CloudConsoleClient } from "libs/utils/http/client/HttpClient";
import { APIResource, APIMethod } from "libs/utils/http/decorator/UrlDecorator";
import { from } from "rxjs";
import { IdentityApplication } from "../../services/ApplicationService";
import { IdentityApplicationCreateRequest } from "pages/home/apis/services/ApiService";

@APIResource(CloudConsoleClient, "publisher/v2/applications")
class ApplicationServiceFactory {
  /**
   * Applies spike/quota config to a deployment (persists `deployment_throttling` via orchestrator redeploy).
   * Body matches publisher `ThrottlingRequest` (quotaEnabled, quotaConfig, spikeArrestEnabled, spikeConfig).
   */
  @APIMethod("/deployment/:deploymentId/throttling")
  public createApiThrottling(deploymentId: string, requestBody: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), requestBody, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data),
    );
  }

  /**
   * Same as `createApiThrottling` for regional deployments (`updateDeploymentV2` / gateway configuration).
   */
  @APIMethod("/deployment/:deploymentId/throttlingV2")
  public createApiThrottlingV2(deploymentId: string, requestBody: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), requestBody, {
        params: {
          deploymentId,
        },
      }).then((value) => value.data),
    );
  }

  @APIMethod("/deployment/:deploymentId/appthrottling/:consumerKey", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createApplicationThrottling(deploymentId: string, consumerKey: string, requestBody: any) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), requestBody, {
        params: {
          deploymentId,
          consumerKey,
        },
      }).then((value) => value.data),
    );
  }

  @APIMethod("/deployment/:deploymentId/appthrottling/:consumerKey", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteApplicationThrottling(
    deploymentId: string,
    consumerKey: string,
  ) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          deploymentId,
          consumerKey,
        },
      }).then((value) => value.data),
    );
  }

  /** Regional deployments: redeploy via publisher updateDeploymentV2 */
  @APIMethod("/deployment/:deploymentId/appthrottlingV2/:consumerKey", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createApplicationThrottlingV2(
    deploymentId: string,
    consumerKey: string,
    requestBody: any,
  ) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), requestBody, {
        params: {
          deploymentId,
          consumerKey,
        },
      }).then((value) => value.data),
    );
  }

  @APIMethod("/deployment/:deploymentId/appthrottlingV2/:consumerKey", {
    options: {
      noTrailingSlash: true,
    },
  })
  public deleteApplicationThrottlingV2(
    deploymentId: string,
    consumerKey: string,
  ) {
    return from(
      CloudConsoleClient.delete<any>(apiMethodContext(arguments), {
        params: {
          deploymentId,
          consumerKey,
        },
      }).then((value) => value.data),
    );
  }

  @APIMethod("/:resourceAppId/versions", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getAllVersions(resourceAppId: string) {
    return from(
      CloudConsoleClient.get<any>(apiMethodContext(arguments), {
        params: {
          resourceAppId,
        },
      }).then((value) => value.data),
    );
  }

  @APIMethod("", {
    options: {
      noTrailingSlash: true,
    },
  })
  public getAllApplication(gatewayName: string, applicationType: string) {
    return from(
      CloudConsoleClient.get<IdentityApplication[]>(
        apiMethodContext(arguments),
        {
          params: {
            gatewayName,
            includeEmailAddress: true,
            type: applicationType,
          },
        },
      ).then((value) => value.data),
    );
  }

  @APIMethod("", {
    options: {
      noTrailingSlash: true,
    },
  })
  public createApplication(
    teamId: Record<string, string>,
    request?: IdentityApplicationCreateRequest,
  ) {
    return from(
      CloudConsoleClient.post<any>(apiMethodContext(arguments), request, {
        params: {
          ...teamId,
        },
      }).then((value) => value.data),
    );
  }
}

const ApplicationService = new ApplicationServiceFactory();

export default ApplicationService;
